import {ErrorCode} from 'presentation/hooks/useGeolocation/types';
import {useReverseGeolocation} from 'presentation/hooks/useReverseGeolocation';
import {translations} from 'presentation/translations-new';
import {useCallback, useEffect, useRef, useState} from 'react';
import {geolocationErrorConfig} from './constants';
import {GeolocationErrorKind} from './types';

const {permissionsNotAllowed} = translations['pt-br'].commons;

export function useAddressFormGeolocation() {
	const {
		permission,
		permissionError,
		reverseGeocodeData,
		geolocationError,
		reverseGeocodeError,
		isReverseGeocodeFetching,
		geoLoading,
		requestLocation: fetchCurrentLocation,
		resetLocation,
	} = useReverseGeolocation();

	const [openInstructions, setOpenInstructions] = useState(false);
	const [openInstabilityError, setOpenInstabilityError] = useState(false);
	const [locationErrorKind, setLocationErrorKind] = useState<GeolocationErrorKind>(null);
	const [isLocating, setIsLocating] = useState(false);
	const requestIdRef = useRef(0);

	const geolocationOrPermissionIsNotSupported =
		geolocationError?.code === ErrorCode.NOT_SUPPORTED ||
		permissionError === permissionsNotAllowed;
	const isLocationBlocked = permission === 'denied';

	const positionUnavailableOrTimeout =
		geolocationError?.code === ErrorCode.POSITION_UNAVAILABLE ||
		geolocationError?.code === ErrorCode.TIMEOUT;

	const startLocationRequest = useCallback(() => {
		setOpenInstructions(false);
		setOpenInstabilityError(false);
		setLocationErrorKind(null);
		setIsLocating(true);
		requestIdRef.current += 1;
		fetchCurrentLocation();
	}, [fetchCurrentLocation]);

	const requestLocation = useCallback(() => {
		if (permission === 'denied') {
			setOpenInstructions(true);
			setIsLocating(false);
			setLocationErrorKind('permissionDenied');
			return;
		}

		startLocationRequest();
	}, [permission, startLocationRequest]);

	const retryLocation = useCallback(() => {
		startLocationRequest();
	}, [startLocationRequest]);

	useEffect(() => {
		if (!geolocationError) {
			return;
		}

		setIsLocating(false);
		const config = geolocationErrorConfig[geolocationError.code];

		if (!config) {
			return;
		}

		setLocationErrorKind(config.kind);

		if (config.openInstructions) {
			setOpenInstructions(true);
			return;
		}

		setOpenInstabilityError(true);
	}, [geolocationError]);

	useEffect(() => {
		if (isReverseGeocodeFetching) {
			return;
		}

		if (reverseGeocodeError) {
			setIsLocating(false);
			setLocationErrorKind('apiError');
			setOpenInstabilityError(true);
			return;
		}

		if (reverseGeocodeData) {
			setIsLocating(false);
		}
	}, [reverseGeocodeData, reverseGeocodeError, isReverseGeocodeFetching]);

	useEffect(() => {
		const locationFlowIsNotPendingOrAlreadySettled =
			!isLocating ||
			geoLoading ||
			isReverseGeocodeFetching ||
			geolocationError ||
			reverseGeocodeError ||
			reverseGeocodeData;

		if (locationFlowIsNotPendingOrAlreadySettled) {
			return undefined;
		}

		const currentRequestId = requestIdRef.current;
		const safetyTimeout = setTimeout(() => {
			if (requestIdRef.current === currentRequestId) {
				setIsLocating(false);
				setLocationErrorKind('timeout');
				setOpenInstabilityError(true);
			}
		}, 20000);

		return () => clearTimeout(safetyTimeout);
	}, [
		isLocating,
		geoLoading,
		isReverseGeocodeFetching,
		geolocationError,
		reverseGeocodeError,
		reverseGeocodeData,
	]);

	const closeInstructions = useCallback(() => {
		setOpenInstructions(false);
	}, []);

	const closeInstabilityError = useCallback(() => {
		setOpenInstabilityError(false);
	}, []);

	const resetGeolocation = useCallback(() => {
		setIsLocating(false);
		setLocationErrorKind(null);
		setOpenInstructions(false);
		setOpenInstabilityError(false);
		resetLocation();
	}, [resetLocation]);

	return {
		isLocating: isLocating || geoLoading || isReverseGeocodeFetching,
		isLocationBlocked,
		locationErrorKind,
		openInstructions,
		openInstabilityError,
		reverseGeocodeData,
		positionUnavailableOrTimeout,
		geolocationOrPermissionIsNotSupported,
		requestLocation,
		retryLocation,
		closeInstructions,
		closeInstabilityError,
		resetGeolocation,
	};
}
